import { DependencyList, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Opts, QueryParams } from "../types";
import { useIsMounted } from "./useIsMounted";

const DEFAULT_ID_FIELD = "id";
const DEFAULT_LABEL_FIELD = "label";

export const ERROR_ITEM = {
  id: "ERROR",
  label: "An error occurred loading these options",
  disabled: true,
};

type Item = {
  id: string | number | boolean;
  label: string;
  [key: string]: any;
};

type ItemArray = Item[] & { isPlaceholder?: undefined | true };

type LabelType = string | ((item: { [key: string]: any }) => string);

type Result = { [key: string]: any }[];

type TypeOfGetter = "GETTER" | "GETTER_WITH_FIELDS" | "GETTER_WITH_PARAMS";

type GetterService = {
  getAll: (opts: Opts) => Promise<Result>;
};

type GetterWithParamsService = {
  getAll: (params: QueryParams, opts: Opts) => Promise<Result>;
};

type GetterWithFieldsArg = {
  service: GetterService;
  id?: string;
  label?: LabelType;
};

type GetterWithFieldsAndParamsArg = {
  service: GetterWithParamsService;
  params: QueryParams;
  id?: string;
  label?: LabelType;
};

type ArgType =
  | GetterService
  | GetterWithFieldsArg
  | GetterWithFieldsAndParamsArg;

export const useLookupWithRefresh = (args: ArgType[], deps: DependencyList = []): ItemArray[] => {
  const token: string =
    useSelector((state: any) => state?.auth?.user?.token) || undefined;

  // the hook returns an array of arrays, one lookup array for every arg
  //  passed in to it, so returns an array of arrays.  So we initialise
  //  with a set of arrays of the appropriate length.
  const defaultItems = Array(args.length).fill(getPlaceholder());
  const [items, setItems] = useState<ItemArray[]>(defaultItems);
  const isMountedRef = useIsMounted();

  const getters = () =>
    Promise.all(
      args.map((arg) => {
        const resolvedArg = resolveArg(arg);
        return resolvedArg.service
          .getAll({ token })
          .then((items) =>
            items.map((item) => ({
              ...item,
              id: item[resolvedArg.id],
              label:
                typeof resolvedArg.label === "function"
                  ? resolvedArg.label(item)
                  : String(item[resolvedArg.label]),
            }))
          )
          .catch(() => [{ ...ERROR_ITEM }]);
      })
    );

  useEffect(() => {
    if (!token) {
      console.warn(`
        useLookup has not found an auth token in redux at the location:
        state -> auth -> user -> token
        The subsequent api call will fail unless it is a non-authed endpoint`);
    }
    getters().then((items) => isMountedRef.current && setItems(items));
  }, deps);

  return items;
};

const resolveArg = (arg: ArgType) => {
  const argAsGetter = arg as GetterService;
  const argAsGetterWithFields = arg as GetterWithFieldsArg;
  const argAsGetterWithParamsAndFields = arg as GetterWithFieldsAndParamsArg;
  const typeOfArg: TypeOfGetter = !!argAsGetterWithParamsAndFields.params
    ? "GETTER_WITH_PARAMS"
    : !!argAsGetterWithFields.service
    ? "GETTER_WITH_FIELDS"
    : "GETTER";

  return typeOfArg === "GETTER_WITH_PARAMS"
    ? {
        service: {
          getAll: (opts: Opts) =>
            argAsGetterWithParamsAndFields.service.getAll(
              argAsGetterWithParamsAndFields.params,
              opts
            ),
        } as GetterService,
        id: argAsGetterWithParamsAndFields.id || DEFAULT_ID_FIELD,
        label: argAsGetterWithParamsAndFields.label || DEFAULT_LABEL_FIELD,
      }
    : typeOfArg === "GETTER_WITH_FIELDS"
    ? {
        service: argAsGetterWithFields.service,
        id: argAsGetterWithFields.id || DEFAULT_ID_FIELD,
        label: argAsGetterWithFields.label || DEFAULT_LABEL_FIELD,
      }
    : {
        service: argAsGetter,
        id: DEFAULT_ID_FIELD,
        label: DEFAULT_LABEL_FIELD,
      };
};

const getPlaceholder = () => {
  const placeHolder = [] as ItemArray;
  placeHolder.isPlaceholder = true;
  return placeHolder;
};
